import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useRef,
  type ElementType,
  type ReactElement,
} from 'react';

import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useChipGroupContext } from '../chip-group/hooks/useChipGroupContext.js';
import { useInternalChipGroupContext } from '../chip-group/hooks/useInternalChipGroupContext.js';
import { ChipGroupActions } from '../chip-group/state/chip-group-state-reducer.js';
import { BaseChip } from './BaseChip.js';
import * as styles from './GroupedChip.sty.js';
import { usePendingFocusAnnouncement } from './hooks/usePendingFocusAnnouncement.js';
import type { ChipProps } from './types/chip.js';

/**
 * Grouped chip component
 * @internal
 */
export const GroupedChip: <E extends ElementType = 'div'>(
  props: ChipProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: ChipProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      size: propSize,
      disabled: propDisabled,
      color = 'neutral',
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;
    const uniqueId = useId();

    const chipRef = useRef<HTMLElement | null>(null);
    const { width } = useContainerSize(chipRef);

    const rootRef = useMergeRefs<HTMLElement | null>([chipRef, forwardedRef]);
    const { visibleKeys } = useChipGroupContext();
    const internalChipGroupContext = useInternalChipGroupContext();
    const {
      disabled: chipGroupDisabled,
      size: chipGroupSize,
      dispatch,
      pendingFocusChip,
      setPendingFocusChip,
    } = internalChipGroupContext || {};

    const disabled = propDisabled ?? chipGroupDisabled ?? false;
    const size = propSize ?? chipGroupSize ?? 'default';

    useEffect(() => {
      dispatch?.({
        type: ChipGroupActions.ADDCHIP,
        payload: { uniqueId, width },
      });

      return () => {
        dispatch?.({
          type: ChipGroupActions.REMOVECHIP,
          payload: {
            uniqueId,
          },
        });
      };
      // only for mount and unmount
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      dispatch?.({
        type: ChipGroupActions.UPDATECHIP,
        payload: { uniqueId, width: width === 0 ? undefined : width },
      });
    }, [dispatch, uniqueId, width]);

    const isVisible = visibleKeys.includes(uniqueId);
    const isPendingFocus = pendingFocusChip?.uniqueId === uniqueId;
    const showFocusRing =
      isPendingFocus && pendingFocusChip?.showFocusRing === true;
    // Disabled chips still need a visible focus ring when programmatically focused
    // and 'neutral' avoids showing an active-state color.
    const pendingFocusRingColor = disabled ? 'neutral' : (color ?? 'neutral');

    usePendingFocusAnnouncement(chipRef, {
      isPendingFocus,
      isVisible,
      disabled,
      uniqueId,
      setPendingFocusChip,
    });

    return (
      <div
        ref={rootRef}
        className={clsx(
          'chip',
          styles.grouped,
          showFocusRing && styles.pendingFocusRing[pendingFocusRingColor],
          {
            [styles.first]: isVisible && visibleKeys.length === 1,
            [styles.hidden]: !isVisible,
          },
        )}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <BaseChip
          {...(remainingProps as ChipProps<E>)}
          disabled={disabled}
          size={size}
          color={color}
        >
          {children}
        </BaseChip>
      </div>
    );
  },
);
